import { URL } from "../constants";
import { apiSlice } from "./apiSlice";

export const bomApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    uploadBom: builder.mutation({
      /**
       * 上传 BOM 文件并解析匹配。
       * @param {FormData} formData 上传表单
       * @returns {{url: string, method: string, body: FormData}}
       */
      query: (formData) => ({
        url: `${URL}/bom/upload`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Bom"],
    }),

    getBomOrder: builder.query({
      /**
       * 获取 BOM 任务详情。
       * @param {{id: string, sessionId: string}} params 查询参数
       * @returns {{url: string, method: string, params: Object}}
       */
      query: ({ id, sessionId }) => ({
        url: `${URL}/bom/${id}`,
        method: "GET",
        params: sessionId ? { sessionId } : undefined,
      }),
      providesTags: ["Bom"],
    }),

    createBomItem: builder.mutation({
      /**
       * 在 BOM 云表末尾新增一条物料。
       * @param {{bomId: string, sessionId: string, normalized?: Object}} params 新增参数
       * @returns {{url: string, method: string, body: Object}}
       */
      query: ({ bomId, sessionId, normalized }) => ({
        url: `${URL}/bom/${bomId}/items`,
        method: "POST",
        body: { sessionId, normalized },
      }),
      invalidatesTags: ["Bom"],
    }),

    deleteBomItems: builder.mutation({
      /**
       * 删除 BOM 云表中选中的物料行。
       * @param {{bomId: string, sessionId: string, itemIds: string[]}} params 删除参数
       * @returns {{url: string, method: string, body: Object}}
       */
      query: ({ bomId, sessionId, itemIds }) => ({
        url: `${URL}/bom/${bomId}/items`,
        method: "DELETE",
        body: { sessionId, itemIds },
      }),
      invalidatesTags: ["Bom"],
    }),

    updateBomItem: builder.mutation({
      /**
       * 更新 BOM 明细数量、选择状态或匹配商品。
       * @param {{bomId: string, itemId: string, sessionId: string, payload: Object}} params 更新参数
       * @returns {{url: string, method: string, body: Object}}
       */
      query: ({ bomId, itemId, sessionId, payload }) => ({
        url: `${URL}/bom/${bomId}/items/${itemId}`,
        method: "PATCH",
        body: {
          ...payload,
          sessionId,
        },
      }),
      invalidatesTags: ["Bom"],
    }),

    rematchBom: builder.mutation({
      /**
       * 重新匹配 BOM 任务。
       * @param {{bomId: string, sessionId: string}} params 重新匹配参数
       * @returns {{url: string, method: string, body: Object}}
       */
      query: ({ bomId, sessionId }) => ({
        url: `${URL}/bom/${bomId}/rematch`,
        method: "POST",
        body: { sessionId },
      }),
      invalidatesTags: ["Bom"],
    }),

    aiRematchBom: builder.mutation({
      /**
       * 使用 AI 为无法匹配的 BOM 行生成待确认候选。
       * @param {{bomId: string, sessionId: string, itemIds?: string[]}} params AI 补配参数
       * @returns {{url: string, method: string, body: Object}}
       */
      query: ({ bomId, sessionId, itemIds }) => ({
        url: `${URL}/bom/${bomId}/ai-rematch`,
        method: "POST",
        body: { sessionId, itemIds },
      }),
      invalidatesTags: ["Bom"],
    }),

    reviewBomAiSuggestion: builder.mutation({
      /**
       * 接受或拒绝单条 AI 补配结果。
       * @param {{bomId: string, itemId: string, sessionId: string, decision: string}} params 审核参数
       * @returns {{url: string, method: string, body: Object}}
       */
      query: ({ bomId, itemId, sessionId, decision }) => ({
        url: `${URL}/bom/${bomId}/items/${itemId}/ai-review`,
        method: "POST",
        body: { sessionId, decision },
      }),
      invalidatesTags: ["Bom"],
    }),

    buildBomCartItems: builder.mutation({
      /**
       * 获取可写入购物车的 BOM 商品项。
       * @param {{bomId: string, sessionId: string, itemIds: string[]}} params 加购参数
       * @returns {{url: string, method: string, body: Object}}
       */
      query: ({ bomId, sessionId, itemIds }) => ({
        url: `${URL}/bom/${bomId}/cart-items`,
        method: "POST",
        body: { sessionId, itemIds },
      }),
    }),
  }),
});

export const {
  useUploadBomMutation,
  useGetBomOrderQuery,
  useCreateBomItemMutation,
  useDeleteBomItemsMutation,
  useUpdateBomItemMutation,
  useRematchBomMutation,
  useAiRematchBomMutation,
  useReviewBomAiSuggestionMutation,
  useBuildBomCartItemsMutation,
} = bomApi;
